import type {
  Middleware,
} from 'redux'
import apiMiddleware from 'middlewares/apiMiddleware'
import rootReducer from './reducers'
import type { History } from 'history'
import { configureStore } from '@reduxjs/toolkit'
import config from 'constants/config'
import type { CallHistoryMethodAction } from 'lib/routing/appRouting'
import type { AnyAppAction } from 'actions/types/ActionTypes'
import { actionSanitisers, stateSanitisers } from './storeSanitise'

interface StoreOptions {
  apiMiddleware?: boolean
  devTools?: boolean
}

export default function buildStore(
  history: History,
  initialState?: Partial<App.State>,
  middlewares: Array<Middleware> = [],
  options: StoreOptions = {
    apiMiddleware: true,
    devTools: true,
  },
) {
  return configureStore({
    reducer: rootReducer(history),
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers({ autoBatch: true }) as any,
    middleware: (getDefaultMiddleware) => {
      const defaults = getDefaultMiddleware({
        thunk: true,
        serializableCheck: false,
        immutableCheck: config.REDUX_IMMUTABILITY_CHECK_ENABLED,
      })

      middlewares.forEach(mid => {
        defaults.push(mid)
      })

      if (options.apiMiddleware) {
        defaults.push(apiMiddleware)
      }

      return defaults
    },
    devTools: options.devTools ? {
      autoPause: true,
      actionSanitizer: (action, idNumber) => actionSanitisers.reduce((action, sanitiser) => (
        sanitiser.enabled ? sanitiser.sanitise(action, idNumber) : action
      ), action),
      stateSanitizer: (state: any, index: number) => stateSanitisers.reduce((state, sanitiser) => (
        sanitiser.enabled ? sanitiser.sanitise(state, index) : state
      ), state),
    } : false,
    preloadedState: initialState as any,
  })
}

export type AppStore = ReturnType<typeof buildStore>
type Dispatchable = AnyAppAction<Parameters<AppStore['dispatch']>[0]> | CallHistoryMethodAction
export type AppDispatch = (action: Dispatchable, ...extraArgs: Array<any>) => any
